import { useState } from 'react';
import {
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  Upload,
  RefreshCw,
  Eye,
  Info,
  Sparkles,
  Trash2,
} from 'lucide-react';
import type { Document, DocumentType } from '../types';
import type { RequiredDocumentConfig } from '../data/collectionRequirements';

interface RequiredDocumentSlotProps {
  config: RequiredDocumentConfig;
  document: Document | null;
  onUpload: () => void;
  onReplace: () => void;
  onRemove: () => void;
  onPreview?: () => void;
  isUploading?: boolean;
  uploadProgress?: { step: string; progress: number };
}

const statusIcons = {
  pending: Clock,
  uploaded: Clock,
  processing: Loader2,
  completed: CheckCircle2,
  failed: XCircle,
  verified: CheckCircle2,
  rejected: XCircle,
  needs_review: AlertTriangle,
};

const statusColors = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },
  uploaded: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
  processing: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
  completed: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  failed: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
  verified: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  rejected: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
  needs_review: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
};

const statusLabels: Record<string, string> = {
  pending: 'Not Uploaded',
  uploaded: 'Uploaded',
  processing: 'AI Processing...',
  completed: 'Processed',
  failed: 'Upload Failed',
  verified: 'Verified',
  rejected: 'Rejected',
  needs_review: 'Needs Review',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RequiredDocumentSlot({
  config,
  document,
  onUpload,
  onReplace,
  onRemove,
  onPreview,
  isUploading,
  uploadProgress,
}: RequiredDocumentSlotProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hasDocument = document !== null;
  const status = document?.status || document?.processing_status || 'pending';
  const isProcessing = status === 'processing' || isUploading;
  const displayStatus = isUploading ? 'processing' : status;
  
  const StatusIcon = statusIcons[displayStatus as keyof typeof statusIcons] || Clock;
  const colors = statusColors[displayStatus as keyof typeof statusColors] || statusColors.pending;
  
  const isStatusLoading = displayStatus === 'processing';

  return (
    <div className={`bg-white rounded-xl border-2 ${hasDocument ? colors.border : 'border-gray-200 border-dashed'} overflow-hidden transition-all`}>
      {/* Main content */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
            hasDocument ? colors.bg : 'bg-gray-100'
          }`}>
            {isStatusLoading ? (
              <Loader2 className={`w-6 h-6 ${colors.text} animate-spin`} />
            ) : hasDocument ? (
              <StatusIcon className={`w-6 h-6 ${colors.text}`} />
            ) : (
              <FileText className="w-6 h-6 text-gray-400" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900">{config.name}</h3>
              {config.required && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Required</span>
              )}
              {!config.required && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Optional</span>
              )}
            </div>

            {hasDocument ? (
              <>
                <p className="text-sm text-gray-600 truncate">{document.document_name || document.file_name}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span>{formatFileSize(document.file_size_bytes || document.file_size || 0)}</span>
                  {document.ai_confidence && (
                    <span className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      AI: {Math.round(document.ai_confidence * 100)}%
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                    {statusLabels[displayStatus] || displayStatus}
                  </span>
                </div>
                
                {/* Upload progress */}
                {isUploading && uploadProgress && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>{uploadProgress.step}</span>
                      <span>{uploadProgress.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">{config.description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasDocument ? (
              <>
                {onPreview && (
                  <button
                    onClick={onPreview}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onReplace}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Replace"
                  disabled={isProcessing}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={onRemove}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  title="Remove"
                  disabled={isProcessing}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={onUpload}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                disabled={isProcessing}
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
            )}
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Info className="w-4 h-4" />
                Purpose
              </h4>
              <p className="text-gray-600">{config.purpose}</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-700 mb-1">Accepted Formats</h4>
              <div className="flex flex-wrap gap-1">
                {config.acceptedFormats.map(format => (
                  <span key={format} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">
                    {format}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Max size: {config.maxSizeMB}MB</p>
            </div>
            
            <div className="md:col-span-2">
              <h4 className="font-medium text-gray-700 mb-1">Examples of Acceptable Documents</h4>
              <ul className="text-gray-600 text-sm list-disc list-inside">
                {config.examples.map((example, idx) => (
                  <li key={idx}>{example}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
